import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Video, Sparkles, Image, Clock, Download, Play, Wand2, Settings2, Gem } from 'lucide-react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import LoginModal from './LoginModal';

// 视频比例选项
const ASPECT_RATIOS = [
  { id: '16:9', label: '16:9', desc: '横屏' },
  { id: '9:16', label: '9:16', desc: '竖屏' },
  { id: '1:1', label: '1:1', desc: '方形' },
];

// 视频时长选项
const DURATIONS = [
  { id: '5s', label: '5秒' },
  { id: '10s', label: '10秒' },
];

// 模型选项（占位）
const MODELS = [
  { id: 'kling-v1', label: 'Kling v1', desc: '快速生成' },
  { id: 'kling-v2', label: 'Kling v2', desc: '高质量' },
  { id: 'sora', label: 'Sora', desc: '即将上线', disabled: true },
];

export default function AIVideoPage() {
  const { t } = useTranslation();
  const { isLoggedIn, showLoginModal, requireAuth, closeLoginModal, handleLoginSuccess } = useAuthGuard();

  const [activeTab, setActiveTab] = useState('generate');
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [selectedRatio, setSelectedRatio] = useState('16:9');
  const [selectedDuration, setSelectedDuration] = useState('5s');
  const [selectedModel, setSelectedModel] = useState('kling-v1');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedVideos, setGeneratedVideos] = useState([]);

  const handleGenerate = () => {
    if (!requireAuth()) return;
    if (!prompt.trim()) return;

    // TODO: 接入实际 API
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      // 模拟生成结果占位
      setGeneratedVideos(prev => [
        { id: Date.now(), prompt, ratio: selectedRatio, duration: selectedDuration, status: 'done' },
        ...prev
      ]);
    }, 2000);
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">

          {/* 页面标题 */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Video className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">AI 视频</h1>
            </div>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              输入文字描述，AI 自动生成高质量视频
            </p>
          </div>

          {/* Tab 切换 */}
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit mx-auto">
            <button
              onClick={() => setActiveTab('generate')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                activeTab === 'generate'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Wand2 className="w-4 h-4" />
              生成视频
            </button>
            <button
              onClick={() => setActiveTab('my-works')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                activeTab === 'my-works'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="w-4 h-4" />
              我的作品
            </button>
          </div>

          {/* 生成视频 Tab */}
          {activeTab === 'generate' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* 左侧：参数设置 */}
              <div className="lg:col-span-1 space-y-4">

                {/* 模型选择 */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-3">
                    <Settings2 className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-semibold text-gray-700">模型</span>
                  </div>
                  <div className="space-y-2">
                    {MODELS.map(model => (
                      <button
                        key={model.id}
                        disabled={model.disabled}
                        onClick={() => !model.disabled && setSelectedModel(model.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-all ${
                          model.disabled
                            ? 'opacity-40 cursor-not-allowed border-gray-100 bg-gray-50'
                            : selectedModel === model.id
                              ? 'border-purple-400 bg-purple-50 text-purple-700'
                              : 'border-gray-200 hover:border-purple-200 hover:bg-purple-50/50'
                        }`}
                      >
                        <span className="font-medium">{model.label}</span>
                        <span className="text-xs text-gray-400">{model.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 比例选择 */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-3">
                    <Image className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-semibold text-gray-700">画面比例</span>
                  </div>
                  <div className="flex gap-2">
                    {ASPECT_RATIOS.map(ratio => (
                      <button
                        key={ratio.id}
                        onClick={() => setSelectedRatio(ratio.id)}
                        className={`flex-1 py-2 rounded-lg border text-xs font-medium transition-all ${
                          selectedRatio === ratio.id
                            ? 'border-purple-400 bg-purple-50 text-purple-700'
                            : 'border-gray-200 text-gray-600 hover:border-purple-200'
                        }`}
                      >
                        <div>{ratio.label}</div>
                        <div className="text-gray-400 font-normal">{ratio.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 时长选择 */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-semibold text-gray-700">视频时长</span>
                  </div>
                  <div className="flex gap-2">
                    {DURATIONS.map(d => (
                      <button
                        key={d.id}
                        onClick={() => setSelectedDuration(d.id)}
                        className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
                          selectedDuration === d.id
                            ? 'border-purple-400 bg-purple-50 text-purple-700'
                            : 'border-gray-200 text-gray-600 hover:border-purple-200'
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 费用说明 */}
                <div className="bg-purple-50 rounded-xl p-3 border border-purple-100 flex items-center gap-2 text-sm text-purple-700">
                  <Gem className="w-4 h-4 flex-shrink-0" />
                  <span>每次生成消耗 <strong>50</strong> 钻石</span>
                </div>
              </div>

              {/* 右侧：提示词输入 + 结果 */}
              <div className="lg:col-span-2 space-y-4">

                {/* 提示词输入 */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    描述你想要的视频内容
                  </label>
                  <textarea
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                    placeholder="例如：一只猫咪在阳光下的草地上奔跑，慢镜头，电影感..."
                    rows={4}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent resize-none"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">{prompt.length} / 500</span>
                  </div>
                </div>

                {/* 负向提示词（可折叠） */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    负向提示词 <span className="text-gray-400 font-normal text-xs">（可选）</span>
                  </label>
                  <textarea
                    value={negativePrompt}
                    onChange={e => setNegativePrompt(e.target.value)}
                    placeholder="描述不想出现的内容，例如：模糊、低质量、变形..."
                    rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent resize-none"
                  />
                </div>

                {/* 生成按钮 */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !prompt.trim()}
                  className={`w-full py-3 rounded-xl font-semibold text-base transition-all duration-300 flex items-center justify-center gap-2 ${
                    isGenerating || !prompt.trim()
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-purple-500 text-white hover:bg-purple-600 shadow-md hover:shadow-lg'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      生成中...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      立即生成
                    </>
                  )}
                </button>

                {/* 生成结果区域 */}
                {generatedVideos.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">生成结果</h3>
                    {generatedVideos.map(video => (
                      <div key={video.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        {/* 视频占位区 */}
                        <div className="bg-gray-100 aspect-video flex items-center justify-center">
                          <div className="text-center text-gray-400">
                            <Play className="w-10 h-10 mx-auto mb-2 opacity-40" />
                            <p className="text-xs">视频预览</p>
                          </div>
                        </div>
                        <div className="p-3 flex items-center justify-between">
                          <p className="text-xs text-gray-500 truncate flex-1 mr-3">{video.prompt}</p>
                          <button className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 flex-shrink-0">
                            <Download className="w-3.5 h-3.5" />
                            下载
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 我的作品 Tab */}
          {activeTab === 'my-works' && (
            <div>
              {/* TODO: 接入 API 后展示用户历史作品 */}
              <div className="text-center py-20 text-gray-400">
                <Video className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-base">暂无作品</p>
                <p className="text-sm mt-1">生成的视频将在这里展示</p>
                <button
                  onClick={() => setActiveTab('generate')}
                  className="mt-4 px-5 py-2 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600 transition-colors"
                >
                  去生成
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      <LoginModal
        isOpen={showLoginModal}
        onClose={closeLoginModal}
        onLogin={handleLoginSuccess}
      />
    </>
  );
}
